/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {map, catchError, tap} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {Observable, of} from "rxjs";
import {APP_CONFIG} from '../app.config';
import {SiteBaseMileage} from "../model/SiteBaseMileage";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {MessageService} from "./message.service";

const httpOptions = {
  headers: new HttpHeaders({'Content-Type': 'application/json'})
};

@Injectable()
export class SiteBaseMileageService {

  private readonly sitesMUrl: string;

  constructor(private http: HttpClient,
              private messageService: MessageService) {
    this.sitesMUrl = inject(APP_CONFIG).baseUrl + '/api/sitebasemileages';

  }

  getSiteMileageBySiteId(siteId: number): Observable<SiteBaseMileage[]> {
    const url = `${this.sitesMUrl}/${siteId}`;
    return this.http.get(url, httpOptions).pipe(
      tap(_ => this.log('get Site Mileage By SiteID "${siteId}"')),
      catchError(this.handleError<SiteBaseMileage[]>('get site mileage by site id'))
    ).pipe(map(response => response as SiteBaseMileage[]));
  }

  deleteSiteMileageByid(siteBaseMileageId: number): Observable<{}> {
    const url = `${this.sitesMUrl}/${siteBaseMileageId}`;
    return this.http.delete(url, httpOptions).pipe(
      tap(_ => this.log('Delete Site Base Mileage By SiteID "${siteBaseMileageId}"')),
      catchError(this.handleError<SiteBaseMileage[]>('Error Delete Site Base Mileage By SiteID'))
    );
  }

  saveSiteBaseMileage(siteMileage: SiteBaseMileage) : Observable<SiteBaseMileage> {
    //add new site based mileage
    return this.http.post(this.sitesMUrl, siteMileage, httpOptions).pipe(
      tap( _ => this.log('post Site Base Mileage model')),
      catchError(this.handleError<SiteBaseMileage>('save site based mileage'))
    ).pipe(map(response => response as SiteBaseMileage));
  }

  updateSiteBaseMileage(siteMileage: SiteBaseMileage) : Observable<SiteBaseMileage> {
    //add new site based mileage
    var id = siteMileage.siteBaseMileageId;
    const url = `${this.sitesMUrl}/sitebasemileages/${id}` ;
    return this.http.put(url, siteMileage, httpOptions).pipe(
      tap( _ => this.log('post Site Base Mileage model')),
      catchError(this.handleError<SiteBaseMileage>('save site based mileage'))
    ).pipe(map(response => response as SiteBaseMileage));
  }
  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead
      if (error.error.errorCode) {
        if (error.error.errorCode !== '404 Not Found')
          alert(error.error.errorCode + " : " + error.error.errorMessage);
      }
      else
        alert(error.status + " : " + error.error.message);

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a HeroService message with the MessageService */
  private log(message: string) {
    this.messageService.add('Site Mileage Service: ' + message);
  }
}
